/*
 * Copyright (c) 2021. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import {
  APPLICATION_STATUS_CONTRACT_BASE,
  APPLICATION_STATUS_CONTRACT_CATEGORY,
  APPLICATION_STATUS_CONTRACT_DESCRIPTION,
  APPLICATION_STATUS_CONTRACT_DETAILS,
  APPLICATION_STATUS_CONTRACT_DOCUMENTS,
  APPLICATION_STATUS_CONTRACT_INSURANCE,
  APPLICATION_STATUS_CONTRACT_PARTNER,
  APPLICATION_STATUS_REFINEMENT,
  APPLICATION_STATUS_CONCERN_SUMMARY,
  APPLICATION_STATUS_CONTRACT_RECOMMENDATION,
  APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION,
  APPLICATION_STATUS_DEBT_COLLECTION,
  APPLICATION_STATUS_DONE,
  APPLICATION_STATUS_POWER_OF_ATTORNEY,
  APPLICATION_STATUS_SUMMARY,
} from "./applicationStatus";
import React from "react";
import { Navigate } from "react-router";
import ProgressBar from "@/src/components/ProgressBar/ProgressBar";
import progressBarsteps from "./progressBarSteps";
import SummaryHintBox from "@/src/components/Form/Common/SummaryHintBox";
import ContractBase from "@/src/components/Form/ContractLaw/ContractBase";
import PowerOfAttorney from "@/src/components/Form/LitigationCase/PowerOfAttorney";
import ContractDetails from "@/src/components/Form/ContractLaw/ContractDetails";
import ContractPartner from "@/src/components/Form/ContractLaw/ContractPartner";
import ContractDescription from "@/src/components/Form/ContractLaw/ContractDescription";
import Insurance from "@/src/components/Form/Common/Insurance/Insurance";
import Summary from "@/src/components/Form/ContractLaw/Summary/Summary";
import DebtCollection from "@/src/components/Form/ContractLaw/VehicleContractDetails/DebtCollection";
import HintBox from "@/src/components/Box/HintBox/HintBox";
import ContractLawCaseSelection from "@/src/components/Form/ContractLaw/ContractLawCaseSelection";
import { getCaseLink } from "@/src/services/productService";
import { FormSteps } from "@/src/types/FormServiceBase";
import PowerOfAttorneyIntroHintBox from "@/src/components/Form/LitigationCase/PowerOfAttorneyIntroHintBox";
import { DocumentsForm } from "@/src/components/Form/DocumentsForm/DocumentsForm";
import { ContractLawContractRecommendation } from "@/src/components/Form/ContractLaw/ContractLawContractRecommendation";
import {
  ContractLawContractRecommendationDescription
} from "@/src/components/Form/ContractLaw/ContractLawContractRecommendationDescription";
import Refinement from "@/src/components/Form/Common/AiAssessment/Refinement";
import ConcernSummary from "@/src/components/Form/Common/AiAssessment/ConcernSummary";

const formSteps: FormSteps = {
  [APPLICATION_STATUS_CONTRACT_CATEGORY]: {
    title: "Antrag - Ihre Vertragsart | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_CATEGORY,
    stepUrl: "vertragsart",
    render: (props) => (
      <>
        <HintBox headline={"Wir kümmern uns um Ihre Vertragsangelegenheit!"}>
          Bitte schildern Sie uns anhand der folgenden Fragen kurz Ihre Situation. Jede Information hilft uns dabei, Sie bestmöglich zu beraten und direkt
          rechtliche Schritte einleiten zu können. Sollten Sie bei Fragen unsicher sein, machen Sie sich hierüber keine Sorgen: Sie können zusätzlich den Fall
          noch frei aus Ihrer Sicht schildern.
        </HintBox>
        <ProgressBar steps={progressBarsteps} active={"contract"} progress={15} />
        <ContractLawCaseSelection {...props} applicationStatus={APPLICATION_STATUS_CONTRACT_CATEGORY} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION]: {
    title: "Antrag - Schilderung | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION,
    stepUrl: "schilderung",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"contract"} progress={15} />
        <ContractLawContractRecommendationDescription {...props} applicationStatus={APPLICATION_STATUS_CONTRACT_RECOMMENDATION_DESCRIPTION} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_RECOMMENDATION]: {
    title: "Antrag - Vorschlag | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_RECOMMENDATION,
    stepUrl: "vorschlag",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"contract"} progress={15} />
        <ContractLawContractRecommendation {...props} applicationStatus={APPLICATION_STATUS_CONTRACT_RECOMMENDATION} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_BASE]: {
    title: "Antrag - Ihr Vertrag | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_BASE,
    stepUrl: "vertrag",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"contract"} progress={15} />
        <ContractBase {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_DETAILS]: {
    title: "Antrag - Details zu Ihrem Vertrag | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_DETAILS,
    stepUrl: "vertragsdetails",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"contract"} progress={15} />
        <ContractDetails {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_PARTNER]: {
    title: "Antrag - Angaben zum Vertragspartner | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_PARTNER,
    stepUrl: "vertragspartner",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"contract"} progress={15} />
        <ContractPartner {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_DEBT_COLLECTION]: {
    title: "Antrag - Angaben zum Inkassounternehmen | Legalbird",
    applicationStatus: APPLICATION_STATUS_DEBT_COLLECTION,
    stepUrl: "inkasso",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"contract"} progress={15} />
        <DebtCollection {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_DESCRIPTION]: {
    title: "Antrag - Beschreibung | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_DESCRIPTION,
    stepUrl: "beschreibung",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"contract"} progress={15} />
        <ContractDescription {...props} applicationStatus={APPLICATION_STATUS_CONTRACT_DESCRIPTION} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_DOCUMENTS]: {
    title: "Antrag - Dokumente | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_DOCUMENTS,
    stepUrl: "unterlagen",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"documents"} progress={47} />
        <DocumentsForm product={props.product} submitProps={props.submitProps} />
      </>
    ),
  },
  [APPLICATION_STATUS_REFINEMENT]: {
    title: "Antrag - Rückfragen | Legalbird",
    applicationStatus: APPLICATION_STATUS_REFINEMENT,
    stepUrl: "rueckfragen",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"documents"} progress={55} />
        <Refinement fieldsToWaitFor={["aiInitialAssessment"]} {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONCERN_SUMMARY]: {
    title: "Antrag - Zusammenfassung | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONCERN_SUMMARY,
    stepUrl: "zusammenfassung",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"documents"} progress={65} />
        <ConcernSummary {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_CONTRACT_INSURANCE]: {
    title: "Antrag - Versicherung | Legalbird",
    applicationStatus: APPLICATION_STATUS_CONTRACT_INSURANCE,
    stepUrl: "versicherung",
    render: (props) => (
      <>
        <ProgressBar steps={progressBarsteps} active={"insurance"} progress={80} />
        <Insurance {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_SUMMARY]: {
    title: "Antrag - Überprüfen | Legalbird",
    applicationStatus: APPLICATION_STATUS_SUMMARY,
    stepUrl: "ueberpruefen",
    render: (props) => (
      <>
        <SummaryHintBox />
        <Summary {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
    title: "Antrag - Vollmacht | Legalbird",
    applicationStatus: APPLICATION_STATUS_POWER_OF_ATTORNEY,
    stepUrl: "vollmacht",
    render: (props) => (
      <>
        <PowerOfAttorneyIntroHintBox {...props} />
        <PowerOfAttorney {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_DONE]: {
    title: "Mein Vertrag | Legalbird",
    applicationStatus: APPLICATION_STATUS_DONE,
    stepUrl: "mein-vertrag",
    render: ({ product }) => <Navigate to={getCaseLink(product)} />,
  },
};

export default formSteps;
